import React from 'react';
import { ArrowLeft, Brain, CheckCircle, Clock, Mail, MessageCircle, AlertTriangle } from 'lucide-react';

const RefundPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen" style={{ background: '#0D1117' }}>
      {/* Header */}
      <div className="py-8" style={{ background: '#111827', borderBottom: '1px solid #1F2937' }}>
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center space-x-3">
            <Brain size={28} className="text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Refund Policy</h1>
              <p className="text-gray-400 text-sm mt-1">Last Updated: February 25, 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Guarantee Banner */}
      <div className="bg-green-50 border-b border-green-200">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center space-x-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle size={30} className="text-green-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-green-900">14-Day 100% Money-Back Guarantee</h2>
            <p className="text-green-700 text-sm mt-0.5">
              We're confident in TradeFix AI. If you're not completely satisfied within 14 days, we'll refund you in full — no questions asked.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-8 text-gray-700 leading-relaxed">

        {/* Overview */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            1. Our Guarantee
          </h2>
          <p>
            At TradeFix AI, customer satisfaction is our top priority. We offer a <strong>14-day, no-questions-asked,
            full refund guarantee</strong> on all new paid subscriptions. We believe in the value our platform
            provides, and we want you to feel confident when you sign up.
          </p>
        </section>

        {/* Eligible Plans */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            2. Eligible Plans
          </h2>
          <p>The 14-day money-back guarantee applies to the following plans:</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-bold text-blue-900 text-lg">Pro Plan</h3>
              <p className="text-blue-800 font-semibold mt-1">₹999 / month</p>
              <div className="flex items-center space-x-2 mt-3">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">Eligible for full refund within 14 days of first purchase</span>
              </div>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
              <h3 className="font-bold text-indigo-900 text-lg">Elite Plan</h3>
              <p className="text-indigo-800 font-semibold mt-1">₹1,999 / month</p>
              <div className="flex items-center space-x-2 mt-3">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">Eligible for full refund within 14 days of first purchase</span>
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            The Free Plan does not involve any charges and is therefore not eligible for a refund.
          </p>
        </section>

        {/* How to Request */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            3. How to Request a Refund
          </h2>
          <p>Requesting a refund is simple. Choose either of the following contact methods:</p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="mailto:krishautomation.services@gmail.com?subject=Refund Request - TradeFix AI&body=Hi, I would like to request a refund for my TradeFix AI subscription.%0A%0ARegistered Email: %0APlan: %0APayment Date: %0AReason (optional):"
              className="flex items-start space-x-4 bg-gray-50 rounded-xl p-5 border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
            >
              <Mail size={24} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Email Us</p>
                <p className="text-blue-600 text-sm mt-0.5">krishautomation.services@gmail.com</p>
                <p className="text-xs text-gray-500 mt-2">Include your registered email, plan name, and payment date. We'll respond within 24 hours.</p>
              </div>
            </a>

            <a
              href="https://wa.me/919373606511?text=Hi, I would like to request a refund for my TradeFix AI subscription."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start space-x-4 bg-gray-50 rounded-xl p-5 border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors"
            >
              <MessageCircle size={24} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">WhatsApp Us</p>
                <p className="text-green-600 text-sm mt-0.5">+91-9373606511 (Message only)</p>
                <p className="text-xs text-gray-500 mt-2">Send us your registered email and plan details. Available Mon–Sat, 10 AM – 6 PM IST.</p>
              </div>
            </a>
          </div>

          <div className="mt-5 bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-800 font-medium">When contacting us, please provide:</p>
            <ol className="list-decimal list-inside mt-2 space-y-1 text-sm text-blue-700 pl-2">
              <li>Your registered email address</li>
              <li>The plan you subscribed to (Pro or Elite)</li>
              <li>Date of payment</li>
              <li>Reason for refund (optional — helps us improve)</li>
            </ol>
          </div>
        </section>

        {/* Processing Time */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            4. Processing Time
          </h2>
          <div className="flex items-start space-x-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <Clock size={24} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-900">5–7 Business Days</p>
              <p className="text-gray-600 text-sm mt-1">
                Once your refund request is approved, the amount will be credited back to your original
                payment method within 5–7 business days. Processing times may vary depending on your bank
                or card issuer.
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-600">
            We will send you a confirmation email once the refund has been initiated. If you do not receive
            the refund within 7 business days, please contact your bank before reaching out to us.
          </p>
        </section>

        {/* Refund Method */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            5. Refund Method
          </h2>
          <p>
            All refunds are processed through <strong>our payment gateway partner</strong>, and
            credited back to your <strong>original payment method</strong>:
          </p>
          <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
            <li>Credit or debit card payments → refunded to the same card</li>
            <li>UPI payments → refunded to the originating UPI account</li>
            <li>Net banking → refunded to the originating bank account</li>
            <li>Wallets → refunded to the originating wallet</li>
          </ul>
          <p className="mt-3">
            We are unable to process refunds to a different payment method than the one used for the
            original transaction.
          </p>
        </section>

        {/* Conditions & Exceptions */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            6. Conditions and Exceptions
          </h2>

          <div className="space-y-3">
            <div className="flex items-start space-x-3 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <AlertTriangle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900 text-sm">Renewals Are Not Eligible</p>
                <p className="text-gray-600 text-sm mt-1">
                  The 14-day guarantee applies only to your <strong>first payment</strong> on a given plan.
                  Monthly renewals are not eligible for refunds. Please cancel your subscription before the
                  renewal date if you do not wish to be charged.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <AlertTriangle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900 text-sm">Time Limit</p>
                <p className="text-gray-600 text-sm mt-1">
                  Refund requests must be submitted within <strong>14 calendar days</strong> of the initial
                  payment date. Requests submitted after this period will not be processed.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <AlertTriangle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900 text-sm">Abuse Prevention</p>
                <p className="text-gray-600 text-sm mt-1">
                  We reserve the right to decline refund requests where we have evidence of policy abuse,
                  such as repeated sign-ups for the purpose of obtaining free access.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <AlertTriangle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900 text-sm">Promotional Purchases</p>
                <p className="text-gray-600 text-sm mt-1">
                  Subscriptions purchased at a special promotional price may have different refund terms,
                  which will be communicated at the time of purchase.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Cancellation */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            7. Cancellation
          </h2>
          <p>
            You can cancel your subscription at any time. To cancel, contact us via email or WhatsApp.
            Upon cancellation:
          </p>
          <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
            <li>Your subscription will remain active until the end of the current billing period.</li>
            <li>You will not be charged for the next billing cycle.</li>
            <li>No partial refunds are provided for the unused portion of a billing period, except within the 14-day guarantee window.</li>
          </ul>
        </section>

        {/* Contact Support */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            8. Contact Support
          </h2>
          <p>
            Our support team is happy to help with any refund or billing questions.
          </p>
          <div className="mt-4 bg-gray-50 rounded-lg p-5 border border-gray-200 space-y-3">
            <p><strong>Krish Automation Services</strong></p>
            <p>
              📧 Email:{' '}
              <a href="mailto:krishautomation.services@gmail.com" className="text-blue-600 hover:underline">
                krishautomation.services@gmail.com
              </a>
            </p>
            <p>
              💬 WhatsApp:{' '}
              <a href="https://wa.me/919373606511" className="text-blue-600 hover:underline">
                +91-9373606511
              </a>{' '}
              (Message only)
            </p>
            <p className="text-sm text-gray-500">Response within 24 hours | Mon–Sat, 10 AM – 6 PM IST</p>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-400 leading-relaxed">
            ⚠️ <strong>Disclaimer:</strong> TradeFix AI is a data analytics and psychology coaching tool for traders. We do not provide investment advice, trading signals, or financial recommendations. All trading decisions are solely the responsibility of the user.
          </p>
        </div>

        {/* Back button */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <ArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
